import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import './ReconDB.css';
import Navbar from '../components/Navbar';
import recondbBanner from '../assets/recondb.png';
import ReconDBSubmitModal from '../components/ReconDBSubmitModal';

const ReconDB = () => {
  const [reconData, setReconData] = useState([]);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('en'); // 'en' or 'zh'
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    const fetchReconData = async () => {
      try {
        const response = await apiClient.get('/recondb/');
        console.log('ReconDB API response:', response.data);
        // Ensure we always set an array
        setReconData(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching ReconDB data:', err);
        setError('Failed to load ReconDB data. Please try again later.');
      }
    };

    fetchReconData();
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'zh' : 'en');
  };

  const openSubmitModal = () => {
    setShowSubmitModal(true);
  };

  const closeModal = () => {
    setShowSubmitModal(false);
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '';
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Filter data based on search term
  const filteredData = Array.isArray(reconData) ? reconData.filter(item => {
    const searchFields = [
      item.title,
      item.platform,
      item.author,
      item.state,
      item.disassembly,
      item.comments
    ];
    
    return searchFields.some(field => 
      field && field.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) : [];

  // Sort filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const aValue = a[sortConfig.key] || '';
    const bValue = b[sortConfig.key] || '';

    // Strip HTML tags for comparison
    const aText = aValue.replace(/<[^>]*>/g, '').toLowerCase();
    const bText = bValue.replace(/<[^>]*>/g, '').toLowerCase();

    if (aText < bText) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aText > bText) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Table header translations
  const tableHeaders = {
    en: {
      title: 'Title',
      platform: 'Platform',
      author: 'Author',
      state: 'State',
      disassembly: 'Disassembly',
      comments: 'Comments'
    },
    zh: {
      title: '标题',
      platform: '平台',
      author: '作者',
      state: '状态',
      disassembly: '拆解',
      comments: '备注'
    }
  };

  if (error) {
    return (
      <div className="recondb-container">
        <Navbar />
        <div className="recondb-content">
          <div className="recondb-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="recondb-container">
      <h1 className="seo-visually-hidden">ReconDB 电子游戏逆向工程档案</h1>
      <Navbar />
      <div className="recondb-content">
        <div className="recondb-banner">
          <img src={recondbBanner} alt="ReconDB Banner" />
        </div>
        
        <div className="language-toggle">
          <button 
            className={`language-btn ${language === 'en' ? 'active' : ''}`}
            onClick={toggleLanguage}
          >
            English
          </button>
          <button 
            className={`language-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={toggleLanguage}
          >
            中文
          </button>
        </div>
        
        <div className="recondb-description">
          {language === 'en' ? (
            <p>The Reverse Engineering Community Network Database (ReconDB) aims to create a master list of community efforts of reverse engineering video game ROMs. Preservation of game development materials has been a longstanding concern, and while no comprehensive solution exists, community- led reverse engineering has become an invaluable resource for scholars. These efforts contribute to the study of video game history and emerging fields like Platform Studies and Critical Code Studies. However, many of these projects are decentralized, often hosted on defunct or under -maintained sites. ReconDB seeks to connect and document these essential contributions in a unified hub. In its initial stage, the project will focus on archiving disassemblies of video games on early game consoles.</p>
          ) : (
            <p>逆向工程社区网络数据库（ReconDB）旨在创建视频游戏ROM社区逆向工程成果的主列表。游戏开发材料的保存长期以来备受关注，尽管尚无全面解决方案，但社区主导的逆向工程已成为学者们的宝贵资源。这些努力不仅助力电子游戏历史研究，更为平台研究（Platform Studies）和批判性代码研究（Critical Code Studies）等新兴领域提供支持。然而，许多项目呈分散状态，常托管于已失效或维护不足的网站。ReconDB致力于将这些重要成果整合记录于统一平台。项目初期将重点归档早期游戏主机的游戏反编译成果。</p>
          )}
        </div>
        
        <div className="recondb-controls">
          <div className="recondb-search-container">
            <input
              type="text"
              className="recondb-search-input"
              placeholder={language === 'en' ? "Search..." : "搜索..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button 
            className="recondb-submit-button"
            onClick={openSubmitModal}
          >
            {language === 'en' ? "Submit Disassembly" : "提交解编"}
          </button>
        </div>
        
        <div className="recondb-table-container">
          <table className="recondb-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} className="sortable-header" data-sorted={sortConfig.key === 'title'}>
                  {tableHeaders[language].title}
                  <span className="sort-indicator">{getSortIndicator('title')}</span>
                </th>
                <th onClick={() => handleSort('platform')} className="sortable-header" data-sorted={sortConfig.key === 'platform'}>
                  {tableHeaders[language].platform}
                  <span className="sort-indicator">{getSortIndicator('platform')}</span>
                </th>
                <th onClick={() => handleSort('author')} className="sortable-header" data-sorted={sortConfig.key === 'author'}>
                  {tableHeaders[language].author}
                  <span className="sort-indicator">{getSortIndicator('author')}</span>
                </th>
                <th onClick={() => handleSort('state')} className="sortable-header" data-sorted={sortConfig.key === 'state'}>
                  {tableHeaders[language].state}
                  <span className="sort-indicator">{getSortIndicator('state')}</span>
                </th>
                <th onClick={() => handleSort('disassembly')} className="sortable-header" data-sorted={sortConfig.key === 'disassembly'}>
                  {tableHeaders[language].disassembly}
                  <span className="sort-indicator">{getSortIndicator('disassembly')}</span>
                </th>
                <th onClick={() => handleSort('comments')} className="sortable-header" data-sorted={sortConfig.key === 'comments'}>
                  {tableHeaders[language].comments}
                  <span className="sort-indicator">{getSortIndicator('comments')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item) => (
                <tr key={item.id}>
                  <td>{item.title}</td>
                  <td>{item.platform}</td>
                  <td dangerouslySetInnerHTML={{ __html: item.author }}></td>
                  <td>{item.state}</td>
                  <td dangerouslySetInnerHTML={{ __html: item.disassembly }}></td>
                  <td dangerouslySetInnerHTML={{ __html: item.comments || '-' }}></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* ReconDB Submit Modal */}
      <ReconDBSubmitModal 
        isOpen={showSubmitModal}
        onClose={closeModal}
        language={language}
      />
    </div>
  );
};

export default ReconDB;
